/**
 * `client-purge` (crossmarc/steward-app#1770 S3, grant G6) — the journal row a firm's
 * "permanently delete this client" writes FIRST, and the only thing the background purge acts
 * on.
 *
 * ## Why a journal row at all (design §3 G5, "the anchor")
 *
 * The façade cannot touch the intake claims table (it holds nothing on it) and cannot delete
 * another firm-side row's per-draft counters, which a draft row deleted on its own would
 * orphan. The retention sweeper's role can. So the façade hands the background half to that
 * role over `ClientPurgeQueue`, and the hand-off is this row, not the queue message. (A
 * client's files are `firm-client-document` rows; the façade removes those itself, in the
 * foreground, before it writes this row.)
 *
 *   - the façade creates the row under the firm admin's OWN bearer token, in the firm's own
 *     workspace (so appcore's ordinary gate, `inst:write` plus `steward:write`, decides who may
 *     write one);
 *   - the queue message carries only `{ firmId, purgeId }` ({@link ClientPurgeMessageSchema});
 *   - the purge handler reads THIS row, with a credential whose session policy names that one
 *     workspace, and deletes only what the row lists and the workspace holds.
 *
 * So a message alone deletes nothing: a forged or replayed one names a row that does not exist
 * (404, acknowledged) or one already `done` (acknowledged). A compromised `apiFn` could still
 * write a journal, but only under a token it was handed for that firm.
 *
 * ## Two shapes, one row (decision D3)
 *
 * While `running` the row names the client and every draft and submission to remove. When the
 * handler finishes it REPLACES the row with the `done` shape, which keeps who asked, when, and
 * the counts, and drops `clientId`, `draftIds` and `intakeIds`. Until 2026-10-07 `clientId` was an HMAC of the
 * address and a re-invite recreated it; it is now minted and a re-invite takes a new one, but the
 * journal's `clientId` still names the deleted record, and keeping it would record "this person
 * was deleted before". appcore's system-plane PUT replaces attributes wholesale, which is
 * what makes the strip real rather than a hidden field.
 *
 * The three id fields are also left OUT of `auditSafe`, so appcore's strict audit rows for this
 * type carry them as `[REDACTED]` markers rather than values (appcore `audit-redaction.ts`: only
 * the platform allow-list and `auditSafe` fields keep their literal value).
 */
import { z } from 'zod';
import { ADVISOR_FIRM_SLUG_PATTERN } from './advisorFirmDirectory.js';
import { ULID_PATTERN } from './intakeMessage.js';

export const CLIENT_PURGE_TYPE_CODE = 'client-purge';

/**
 * The most drafts, and separately the most submissions, one journal may list. A client with
 * more than this is not a shape the product produces (one address, one firm), and an unbounded
 * array is an unbounded run inside a handler with a fixed timeout. The façade refuses to write a
 * longer list and the handler refuses to read one.
 */
export const CLIENT_PURGE_MAX_IDS = 100;

export const CLIENT_PURGE_STATES = ['running', 'done'] as const;
export type ClientPurgeState = (typeof CLIENT_PURGE_STATES)[number];

const slug = z.string().regex(ADVISOR_FIRM_SLUG_PATTERN);
const ulid = z.string().regex(ULID_PATTERN);
const userId = z.string().min(1).max(128);
const instant = z.iso.datetime();
const ids = z.array(ulid).max(CLIENT_PURGE_MAX_IDS);
const count = z.number().int().nonnegative();

/** The journal while the background half has work left. */
export const ClientPurgeRunningSchema = z.strictObject({
  firmId: slug,
  /** The `advisor-client` row's id. Matched against a submission's WRITER-stamped `clientId`
   *  before that submission is deleted. Dropped at completion (D3). */
  clientId: z.string().min(1).max(200),
  /** `intake-draft` instance ids, which are also the draft ids the claims table is keyed by. */
  draftIds: ids,
  /** `advisor-intake` instance ids. */
  intakeIds: ids,
  requestedBy: userId,
  requestedAt: instant,
  state: z.literal('running'),
});
export type ClientPurgeRunning = z.infer<typeof ClientPurgeRunningSchema>;

/** The journal once the background half finished: the kept deletion record. No client id, no
 *  draft or submission id, no name or address. */
export const ClientPurgeDoneSchema = z.strictObject({
  firmId: slug,
  requestedBy: userId,
  requestedAt: instant,
  state: z.literal('done'),
  completedAt: instant,
  draftsPurged: count,
  intakesPurged: count,
});
export type ClientPurgeDone = z.infer<typeof ClientPurgeDoneSchema>;

export type ClientPurgeRead =
  | { kind: 'running'; journal: ClientPurgeRunning }
  | { kind: 'done'; journal: ClientPurgeDone }
  | { kind: 'unreadable' };

/**
 * A journal completed before crossmarc/steward-app#2310 carries `statementsDeleted`, a count of
 * objects the purge removed from a bucket the stack no longer manages. Stored rows exist on dev
 * and prod, and the `done` shape is strict, so without this the reader would call every one of
 * them `unreadable` and the purge and the status route would refuse a finished journal. The
 * number is dropped on read: nothing reports it, and nothing writes it any more.
 */
function withoutLegacyStatementsDeleted(value: unknown): unknown {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return value;
  const { statementsDeleted: _legacy, ...rest } = value as Record<string, unknown>;
  return rest;
}

/** Reads a stored `client-purge` row. Anything that is neither shape exactly is `unreadable`,
 *  and the purge handler deletes nothing for an unreadable journal. */
export function readClientPurge(value: unknown): ClientPurgeRead {
  const running = ClientPurgeRunningSchema.safeParse(value);
  if (running.success) return { kind: 'running', journal: running.data };
  const done = ClientPurgeDoneSchema.safeParse(withoutLegacyStatementsDeleted(value));
  if (done.success) return { kind: 'done', journal: done.data };
  return { kind: 'unreadable' };
}

export interface ClientPurgeCounts {
  draftsPurged: number;
  intakesPurged: number;
}

/**
 * The `done` attributes for a finished journal. Built from the running row by NAMING the kept
 * fields rather than by deleting the dropped ones, so a field added to the running shape later
 * is dropped by default instead of kept by default.
 */
export function completedClientPurgeAttributes(
  running: ClientPurgeRunning, counts: ClientPurgeCounts, completedAt: Date,
): ClientPurgeDone {
  return ClientPurgeDoneSchema.parse({
    firmId: running.firmId,
    requestedBy: running.requestedBy,
    requestedAt: running.requestedAt,
    state: 'done',
    completedAt: completedAt.toISOString(),
    draftsPurged: counts.draftsPurged,
    intakesPurged: counts.intakesPurged,
  });
}

/**
 * The `ClientPurgeQueue` message. Two ids and a version, nothing else: everything the handler
 * acts on comes from the journal row it names, read in that firm's workspace.
 */
export const CLIENT_PURGE_MESSAGE_VERSION = 1;

export const ClientPurgeMessageSchema = z.strictObject({
  v: z.literal(CLIENT_PURGE_MESSAGE_VERSION),
  /** The firm's slug, resolved by the handler through the published firm directory to the
   *  one workspace its credential may name. */
  firmId: slug,
  /** The journal's appcore instance id. */
  purgeId: ulid,
});
export type ClientPurgeMessage = z.infer<typeof ClientPurgeMessageSchema>;
